/*
 * Copyright (c) 2023, Salesforce, Inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    Box,
    Center,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {Link as RouteLink} from 'react-router-dom'
import {useBreakpointValue} from '@salesforce/retail-react-app/app/components/shared/ui'

/**
 * The Swatch Component displays item inside `SwatchGroup`. For proper keyboard accessibility,
 * ensure that the rendered elements can receive keyboard focus, and are immediate siblings.
 */
const Swatch = ({
    children,
    id,
    disabled,
    isNotifyMe,
    href,
    label,
    name,
    selected,
    isQuickView = false,
    isProductTile = false,
    isQuickAdd = false,
    isPDP = false,
    isFindInStore = false,
    isFocusable,
    value,
    handleSelect,
    variant = 'square',
    giftCardType = false,
    isFirstColorSwatch = false,
    isGiftPage = false,
    onMouseEnter,
    onMouseLeave,
    circleStyles
}) => {
    const styles = useMultiStyleConfig('SwatchGroup', {
        variant,
        disabled,
        isNotifyMe,
        selected,
        isQuickView,
        isProductTile,
        isQuickAdd,
        isPDP,
        isFindInStore,
        giftCardType,
        isGiftPage,
        id
    })
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const [selectHandlers, setSelectHandlers] = useState({})

    const onSelect = useCallback(
        (e) => {
            e.preventDefault()
            if (selected) {
                return
            }
            handleSelect(value)
        },
        [handleSelect, selected, value]
    )

    const onSelectedLinkClick = useCallback(
        (e) => {
            if (!selected) {
                return
            }

            e.preventDefault()
            e.stopPropagation()
        },
        [selected]
    )

    useEffect(() => {
        if (!handleSelect) {
            return
        }

        setSelectHandlers({
            onClick: onSelect
        })
    }, [onSelect, isDesktop, value])

    const finalProps = href ? {} : selectHandlers

    // Extract swatchContent to prevent it from being passed to DOM element
    const {swatchContent, ...swatchStyles} = styles.swatch || {}

    return (
        <Button
            className="swatch"
            {...(variant !== 'circle' ? swatchStyles : {})}
            {...(variant !== 'circle' ? {sx: {...swatchContent}} : {})}
            as={href ? RouteLink : 'button'}
            to={href}
            replace={href ? true : undefined}
            aria-label={name}
            aria-checked={selected}
            variant={variant === 'circle' ? 'outlineCircle' : 'outline'}
            pl={variant === 'circle' && isFirstColorSwatch ? 0 : undefined}
            role="radio"
            // No bg transition in quick view so OOS swatches gray out instantly (no fade).
            transition={variant !== 'circle' && isQuickView ? 'none' : undefined}
            // To mimic the behavior of native radio inputs, only one input should be focusable.
            // (The rest are selectable via arrow keys.)
            tabIndex={isFocusable ? 0 : -1}
            {...finalProps}
            onClick={href ? onSelectedLinkClick : finalProps.onClick}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            {...(variant === 'circle' && circleStyles ? circleStyles : {})}
        >
            {variant === 'circle' ? (
                <Center
                    {...swatchStyles}
                    sx={{...swatchContent}}
                    width={{base: isProductTile ? '12px' : '24px', lg: '16px'}}
                    height={{base: isProductTile ? '12px' : '24px', lg: '16px'}}
                    {...(circleStyles || {})}
                >
                    <Center {...styles.swatchButton} className="swatchButton">
                        {children}
                        {label && <Box>{label}</Box>}
                    </Center>
                </Center>
            ) : (
                <Center {...styles.swatchButton} className="swatchButton">
                    {children}
                    {label && <Box>{label}</Box>}
                </Center>
            )}
        </Button>
    )
}

Swatch.displayName = 'Swatch'

Swatch.propTypes = {
    /**
     * The children to be rendered within swatch item.
     */
    children: PropTypes.oneOfType([PropTypes.node, PropTypes.string]),
    /**
     * Indicates whether the option is disabled
     */
    disabled: PropTypes.bool,
    /**
     * Indicates whether the option is selected.
     * This props is provided internally by SwatchGroup
     */
    selected: PropTypes.bool,
    /**
     * The shape of the Swatch
     */
    variant: PropTypes.oneOf(['square', 'circle']),
    /**
     * The label of the option.
     */
    label: PropTypes.string,
    /**
     *  The url of this option
     */
    href: PropTypes.string,
    /**
     * The display value for each swatch
     */
    name: PropTypes.string,
    /**
     * The value for the option.
     */
    value: PropTypes.string,
    /**
     * Whether the swatch can receive tab focus
     */
    isFocusable: PropTypes.bool,
    /**
     * This function is called whenever the mouse enters the swatch on desktop or when clicked on mobile.
     * The values is passed as the first argument.
     */
    handleSelect: PropTypes.func,
    isQuickView: PropTypes.bool,
    isProductTile: PropTypes.bool,
    isPDP: PropTypes.bool,
    isFindInStore: PropTypes.bool,
    isFirstColorSwatch: PropTypes.bool,
    /**
     * Style overrides applied to the outer Button and inner circle container
     * when `variant === 'circle'`. Lets callers (e.g. SingleColorSwatch) shrink
     * or restyle the circle swatch without forking the theme variant.
     */
    circleStyles: PropTypes.object
}

export default Swatch
